/**
 * Leviathan Web - Protected Route Component
 * Quasar Prime LLC
 *
 * Wraps routes that require authentication
 */

import { Navigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { isAdminRole } from '@/constants/userRoles';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-base-dark bg-grid-pattern flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-primary font-mono text-sm uppercase tracking-wider">
            Initializing System...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/**
 * Wraps routes that require an administrator.
 *
 * This is presentation only, and deliberately so — every route it guards is
 * also `requireAdmin` on the server, which is what actually stops anything.
 * Hiding a control a non-admin cannot use keeps them from discovering the
 * refusal by clicking it; it is not the refusal.
 */
export const AdminRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user } = useAuth();

  if (!isAdminRole(user?.role)) {
    return (
      <div className="p-6">
        <div className="flex items-start space-x-3 p-6 bg-slate-800 border border-slate-700 max-w-2xl">
          <ShieldAlert className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold text-slate-100 mb-1">Administrators only</h2>
            <p className="text-sm text-slate-400">
              Managing users, permission groups, and data import/export needs an administrator
              account. Ask whoever administers this instance if you need access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
